"use client";

/**
 * Electronics Shop Product Card
 *
 * Mirrors the POS product card (components/pos/FashionProductCard.tsx) for an
 * image-forward storefront layout, adapted to the Kuttos Convex data model.
 *
 * Design:
 * - White card, rounded-2xl, subtle border, NO outer padding (overflow-hidden)
 * - Edge-to-edge 3:4 image (object-cover), bg-gray-50 — fills the full card width
 * - Floating "Buy" button inside image area (bottom-right); no stock badge (POS-only)
 * - Compact info strip: uppercase category, single-line name, then one row of
 *   price + colour dots (ColorSwatch) — like the POS card. No separate quick-view
 *   icon (the card already links to the product page).
 */

import { useState } from "react";
import { discountPct } from "@/lib/config/sale";
import { buyActionFor, choicesOf } from "@/lib/config/productOptions";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ShoppingCart } from "lucide-react";
import { ShopProduct } from "@/lib/types/shop";
import { useCartStore } from "@/lib/store/cartStore";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { toast } from "@/lib/toast";
import { ColorSwatch } from "@/components/ui/ColorSwatch";
import { ElectronicsVariantSelector } from "./ElectronicsVariantSelector";
import { HirePriceTag } from "./HirePriceTag";
import { WebsitePerkTags } from "@/components/product/WebsitePerks";
import { ConditionBadge } from "@/components/product/ConditionBadge";
import { isForHire, isHireOnly } from "@/lib/config/hire";

interface ElectronicsShopProductCardProps {
  product: ShopProduct;
  className?: string;
}

export function ElectronicsShopProductCard({
  product,
  className,
}: ElectronicsShopProductCardProps) {
  const [hoveredIndex, setHoveredIndex] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);

  const { initializeSession, openCart, sessionId } = useCartStore();
  // Add via the AUTHENTICATED Convex mutation (same as the product page + the
  // fashion card). The cart store's addItem uses a tokenless HTTP client that
  // writes to the GUEST cart even when signed in, so a logged-in shopper saw
  // "added" but an empty drawer (the drawer reads the account cart).
  const addToCartMutation = useMutation(api.cart.core.addToCart);

  // Electronics variant axes (colour / storage-spec). When they offer a
  // choice, the Buy button opens the selector (like POS + the product page)
  // instead of blindly adding the base product with no variant.
  const ecColors = ((product as any).electronicsAttributes?.colors as any[]) || [];
  const ecStorage =
    ((product as any).electronicsAttributes?.storageOptions as string[]) || [];
  const hasVariants = ecColors.length > 0 || ecStorage.length > 0;
  // Options the selector cannot show go to the product page; a single option
  // is added as itself (lib/config/productOptions.ts).
  const buy = buyActionFor(product, choicesOf(ecColors.length, ecStorage.length));
  const router = useRouter();
  const brandId = (product.brandId || "") as Id<"brands">;

  // Calculate stock
  const calculateTotalStock = (): number => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce(
        (total, variant) => total + (variant.stock || 0),
        0
      );
    }
    return product.totalStock || product.stock || 0;
  };

  const actualTotalStock = calculateTotalStock();
  const isInStock = product.inStock ?? actualTotalStock > 0;
  // Pre-order: a flagged product is purchasable at 0 stock (recorded as a
  // pre-order at checkout — stock allocated when it arrives).
  const isPreorder = !!(product as any).preorder?.enabled;

  // Category label
  const categoryLabel =
    product.subcategoryName || product.category || "Accessory";

  // Product URL
  const productUrl = `/product/${product.slug || product._id}`;

  // Image
  const currentImage =
    product.images && product.images.length > 0
      ? product.images[0]
      : "/images/placeholder-product.jpg";

  // Handle Add to Cart — wired to Convex
  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if ((!isInStock && !isPreorder) || adding) return;

    // Variant products (colour / storage) → open the selector dialog, like the
    // POS + product-details flow, so the right variant + price are recorded.
    if (buy.kind === "pick") {
      setShowVariantModal(true);
      return;
    }
    if (buy.kind === "page") {
      router.push(productUrl);
      return;
    }

    setAdding(true);
    try {
      // Ensure session is initialized for guest users
      initializeSession();

      await addToCartMutation({
        productId: product._id as Id<"products">,
        brandId,
        quantity: 1,
        // Its only option, when it has one: the server prices it from the id.
        ...(buy.kind === "option"
          ? { variantId: buy.variantId, selectedVariants: buy.selectedVariants }
          : {}),
        sessionId: sessionId || undefined,
      });

      toast.success(`${product.name} added to cart`);
      openCart();
    } catch (error: any) {
      toast.error(error.message || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  // Confirmed from the variant selector → add with the canonical variantId
  // (the server resolves the per-variant price). Errors propagate to the modal.
  const handleVariantConfirm = async (
    quantity: number,
    variantId: string | undefined,
    selectedVariants: Record<string, any> | undefined,
  ) => {
    initializeSession();
    await addToCartMutation({
      productId: product._id as Id<"products">,
      brandId,
      quantity,
      variantId,
      selectedVariants: selectedVariants as any,
      sessionId: sessionId || undefined,
    });
    toast.success(`${product.name} added to cart`);
    openCart();
  };

  return (
    <div
      className={`group relative flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-xs hover:shadow-lg transition-all duration-300 ${className || ""}`}
      onMouseEnter={() => setHoveredIndex(true)}
      onMouseLeave={() => setHoveredIndex(false)}
    >
      {/* Card Image Container — edge-to-edge 3:4 (matches the POS card) */}
      <Link
        href={productUrl}
        className="block relative w-full aspect-3/4 overflow-hidden bg-gray-50"
      >
        <Image
          src={currentImage}
          alt={product.name}
          fill
          unoptimized
          className={`
            object-cover transition-transform duration-500 ease-out
            ${hoveredIndex ? "scale-105" : "scale-100"}
          `}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
        />

        {/* On sale: how much off, top-left (as on the fashion card). */}
        {(() => {
          const off = discountPct(product.price, product.comparePrice);
          return off ? (
            <span className="absolute top-2 left-2 z-10 rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-bold text-white">-{off}%</span>
          ) : null;
        })()}

        {/* New or used, in the shop's own words — nothing for plain New
            (lib/config/productCondition.ts; Bagz Phones, Oct 2026). */}
        <ConditionBadge product={product} className="absolute bottom-2 left-2 z-10 max-w-[55%]" />

        {/* Out-of-stock / pre-order overlay */}
        {!isInStock && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className={isPreorder ? "bg-amber-400 text-amber-950 font-semibold text-xs px-2 py-1 rounded-full" : "text-white font-semibold text-sm"}>
              {isPreorder ? "Pre-order" : "Out of Stock"}
            </span>
          </div>
        )}

        {/* Quick Add Button - Floating (also shown for pre-order at 0 stock).
            A hire-only item is never bought: the pill says Hire and the card's
            link takes the visitor to the page, where the request lives. */}
        {isHireOnly(product) ? (
          <span className="absolute bottom-2 right-2 rounded-full bg-amber-500 text-white px-2.5 py-1.5 text-[10px] font-semibold shadow-md">
            Hire
          </span>
        ) : ((isInStock || isPreorder) && (
          <button
            onClick={handleAddToCart}
            disabled={adding}
            className={`
              absolute bottom-2 right-2 rounded-full 
              shadow-md border transform transition-all duration-300 flex items-center justify-center
              ${adding
                ? "bg-green-500 border-green-500 text-white p-2 scale-110"
                : "bg-white text-black border-gray-100 hover:bg-black hover:text-white hover:border-black px-2.5 py-1.5 gap-1"
              }
            `}
            aria-label="Add to cart"
          >
            <div className="relative">
              <ShoppingCart className="w-4 h-4" strokeWidth={2.5} />
            </div>
            {!adding && (
              <span className="text-[10px] font-semibold">Buy</span>
            )}
          </button>
        ))}
      </Link>

      {/* Product Info — compact POS-style strip: category · name · price + colours */}
      <div className="px-2.5 py-2">
        {/* Category */}
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide truncate mb-0.5">
          {categoryLabel}
        </p>

        {/* Product name — full width on its own line so the variant pills can
            never squeeze it down to nothing. */}
        <Link href={productUrl} className="block min-w-0">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 leading-tight truncate group-hover:text-blue-600 transition-colors">
            {product.name}
          </h3>
        </Link>

        {/* Variant/spec pills (storage/size axis) on their OWN line below the
            name — like the POS card's size pills, but the name keeps precedence. */}
        {ecStorage.length > 0 && (
          <div className="mt-1 flex items-center flex-wrap gap-0.5">
            {ecStorage.slice(0, 4).map((spec, i) => (
              <span
                key={i}
                className="text-[9px] leading-none px-1 py-0.5 rounded font-medium bg-gray-100 text-gray-600 border border-gray-200"
              >
                {spec}
              </span>
            ))}
            {ecStorage.length > 4 && (
              <span className="text-[9px] leading-none font-medium text-gray-400">
                +{ecStorage.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Price + colour dots — one row (no separate quick-view; the card links to the product) */}
        <div className="mt-1.5 flex items-center justify-between gap-2">
          {isHireOnly(product) ? (
            <HirePriceTag product={product} solo />
          ) : (
            <p className="text-sm font-bold text-gray-900 whitespace-nowrap">
              {Math.floor(product.price || 0).toLocaleString()}{" "}
              <span className="text-[11px] font-normal text-gray-500">
                {product.currency || "UGX"}
              </span>
              {discountPct(product.price, product.comparePrice) ? (
                <span className="ml-1 text-[10px] font-normal text-gray-400 line-through">{Math.floor(product.comparePrice || 0).toLocaleString()}</span>
              ) : null}
            </p>
          )}

          {ecColors.length > 0 && (
            <div className="flex items-center gap-1 shrink-0">
              {ecColors.slice(0, 4).map((color: any, index: number) => (
                <ColorSwatch
                  key={index}
                  hex={color.hex || "#808080"}
                  isMulticolor={color.isMulticolor}
                  multicolorList={color.multicolorList}
                  size={12}
                />
              ))}
              {ecColors.length > 4 && (
                <span className="text-[10px] text-gray-400">
                  +{ecColors.length - 4}
                </span>
              )}
            </div>
          )}
        </div>
        {/* For hire, beside the selling price (hire-only items show the rate above instead). */}
        {isForHire(product) && !isHireOnly(product) && (
          <div className="mt-1">
            <HirePriceTag product={product} />
          </div>
        )}
        {/* Free delivery, and its add-ons (website, Oct 2026). */}
        <WebsitePerkTags product={product} className="mt-1" />
      </div>

      {/* Variant selector — opened by the Buy button for products with
          colour/storage axes (electronics). */}
      {hasVariants && (
        <ElectronicsVariantSelector
          isOpen={showVariantModal}
          onClose={() => setShowVariantModal(false)}
          product={product}
          onConfirm={handleVariantConfirm}
        />
      )}
    </div>
  );
}
